import { Component, EventEmitter, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, Subject } from 'rxjs';
import { ColumnModel, FullTableActionModel, FullTableComponent } from '@overflo1/full-table';
import { QueryJoin, SCondition } from '@nestjsx/crud-request';
import { ConfirmationDialogComponent, SnackBarService } from '@overflo1/frame-frontend';
import { MachineModel } from '../../models/machine.model';
import { MachineService } from '../../services/machine/machine.service';
import { MachineTranslations } from 'src/app/frame-entity-config/machine';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-machine-deleted-list',
  standalone: true,
  imports: [FullTableComponent],
  templateUrl: './machine-deleted-list.component.html',
})
export class MachineDeletedListComponent implements OnInit {
  readonly MachineTranslations = MachineTranslations;
  machineTableColumnList: ColumnModel[] = [];
  machineTableSearch: SCondition = { deletedAt: { $notnull: true } };
  machineTableJoin: QueryJoin[] = [{ field: 'family' }];
  machineTableQuickSearchColumns: string[] = ['description', 'uid'];
  machineTableActions = new EventEmitter<void | FullTableActionModel>();
  private refreshSubject = new Subject<void>();
  refresh = this.refreshSubject.asObservable();

  constructor(
    private router: Router,
    private machineService: MachineService,
    private snackBarService: SnackBarService,
    private matDialog: MatDialog,
    private datePipe: DatePipe,
  ) {
  }

  ngOnInit(): void {
    this.machineTableColumnList = [
      {
        def: 'uid',
        name: MachineTranslations.fields.uid,
        value: (element: MachineModel) => element.uid,
        sort: true,
        type: 'string',
      },
      {
        def: 'description',
        name: MachineTranslations.fields.description,
        value: (element: MachineModel) => element.description ?? '',
        sort: true,
        type: 'string',
      },
      {
        def: 'group',
        name: MachineTranslations.fields.group,
        value: (element: MachineModel) => element.group ?? '',
        sort: true,
        type: 'string',
      },
      {
        def: 'family.name',
        name: MachineTranslations.fields.family,
        value: (element: MachineModel) => element.family?.enabled ? element.family.name : '',
        sort: false,
        type: 'string',
      },
      {
        def: 'deletedAt',
        name: MachineTranslations.fields.deletedAt,
        value: (element: MachineModel) => element.deletedAt
          ? (this.datePipe.transform(element.deletedAt, environment.DATE_TIME_FORMAT, undefined, environment.DATE_LOCALE) ?? '')
          : '',
        sort: true,
        type: 'date',
      },
      {
        def: 'actions',
        name: 'Azioni',
        type: 'string',
        value: [
          { type: 'Modifica', icon: 'edit' },
          { type: 'Ripristina', icon: 'restore' },
        ] as FullTableActionModel[],
        width: 100,
      },
    ];

    this.machineTableActions.subscribe(async (action: FullTableActionModel | void) => {
      if (!action) {
        return;
      }
      const element = action.element as MachineModel;
      switch (action.type) {
        case 'Modifica':
          await this.router.navigate(['/frame/entity/machine', element.id]);
          break;
        case 'Ripristina':
          await this.confirmAndRestore(element);
          break;
      }
    });
  }

  private async confirmAndRestore(machine: MachineModel): Promise<void> {
    const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Ripristina macchina',
        description: `Vuoi ripristinare la macchina con UID "${machine.uid}"?`,
      },
    });
    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }
    await this.machineService.restoreMachine(machine.id);
    this.snackBarService.openInfoSnackBar('Macchina ripristinata con successo');
    this.refreshSubject.next();
  }
}
